/**
 * Página da campanha — /campanhas/:id.
 *
 * A tela é organizada na ordem da decisão que a EGBA toma:
 *
 *   1. **Quem recebe** — o público com números reais (total, alcançáveis por
 *      canal, opt-in) e uma amostra nominal. Editável aqui, porque público
 *      errado é o erro mais caro: a régua certa no público errado cobra quem
 *      não deve.
 *   2. **O que recebe** — a régua no canvas, com validação contínua.
 *   3. **O que aconteceria** — a simulação, que roda o dispatch inteiro
 *      (gate eleitoral, opt-in, supressão) sem gravar nada.
 *   4. **O que de fato saiu** — o log de envios, com motivo de bloqueio. A
 *      tela antiga nem mostrava; o operador disparava e ficava cego.
 *
 * Disparar continua sendo decisão: simulação à vista e confirmação digitando o
 * nome da campanha — "tem certeza?" deixou de proteger alguém há muito tempo.
 */

import { SEGMENTOS, type Etapa, type TipoEtapa } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api, type Categoria, type Item, type Lista } from '../../api/cliente'
import { Canvas } from '../../componentes/regua/Canvas'
import { Inspetor } from '../../componentes/regua/Inspetor'
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Modal,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../../componentes/ui'
import { useEditorRegua } from './useEditorRegua'
import {
  NOMES_MOTIVO,
  TONS_STATUS,
  n,
  type AmostraLead,
  type Campanha,
  type DefinicaoPublico,
  type Envio,
  type ResultadoSimulacao,
  type ResumoPublico,
} from './tipos'

const PALETA: Array<{ tipo: TipoEtapa; nome: string }> = [
  { tipo: 'gatilho', nome: 'Gatilho' },
  { tipo: 'acao', nome: 'Ação' },
  { tipo: 'espera', nome: 'Espera' },
  { tipo: 'condicao', nome: 'Condição' },
  { tipo: 'divisao_ab', nome: 'Teste A/B' },
  { tipo: 'fim', nome: 'Fim' },
]

const ESTAGIOS = [
  { valor: 'novo', texto: 'Novas' },
  { valor: 'qualificado', texto: 'Qualificadas' },
  { valor: 'em_negociacao', texto: 'Em negociação' },
  { valor: 'ganho', texto: 'Ganhas' },
  { valor: 'perdido', texto: 'Perdidas' },
]

const TONS_ENVIO: Record<string, 'neutro' | 'sucesso' | 'atencao' | 'perigo'> = {
  enviado: 'sucesso',
  pendente: 'neutro',
  bloqueado: 'atencao',
  falhou: 'perigo',
}

export function DetalheCampanha() {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const qc = useQueryClient()

  const r = useEditorRegua(id!)
  const campanha = r.campanha

  const [confirmando, setConfirmando] = useState(false)
  const [confirmacao, setConfirmacao] = useState('')
  const [simulacao, setSimulacao] = useState<ResultadoSimulacao | null>(null)
  const [resultadoDisparo, setResultadoDisparo] = useState<{
    candidatos: number
    inscritos: number
    jaEstavam: number
  } | null>(null)

  // ------------------------------------------------------------- público ---
  const [publico, setPublico] = useState<DefinicaoPublico>({})
  const [publicoSujo, setPublicoSujo] = useState(false)
  const publicoAdotado = useRef(false)

  useEffect(() => {
    if (!campanha || publicoAdotado.current) return
    publicoAdotado.current = true
    setPublico(campanha.publicoAlvo ?? {})
  }, [campanha])

  const chavePublico = useMemo(() => JSON.stringify(publico), [publico])

  const { data: previaPublico, isFetching: contandoPublico } = useQuery({
    queryKey: ['campanha-publico', chavePublico],
    queryFn: () =>
      api.post<Item<{ resumo: ResumoPublico; amostra: AmostraLead[] }>>(
        '/api/v1/campanhas/publico',
        publico,
      ),
    enabled: publicoAdotado.current,
  })

  const salvarPublico = useMutation({
    mutationFn: () => api.put<Item<Campanha>>(`/api/v1/campanhas/${id}`, { publicoAlvo: publico }),
    onSuccess: () => {
      setPublicoSujo(false)
      void qc.invalidateQueries({ queryKey: ['campanha', id] })
      void qc.invalidateQueries({ queryKey: ['campanhas'] })
    },
    onError: (e: Error) => r.setErro(e.message),
  })

  function mudarPublico(mudanca: Partial<DefinicaoPublico>): void {
    setPublico((p) => {
      const novo = { ...p, ...mudanca }
      // Chave sem valor sai do objeto — `{estagio: []}` filtra diferente de
      // "sem filtro" aos olhos de quem lê o JSON depois.
      for (const k of Object.keys(novo) as Array<keyof DefinicaoPublico>) {
        const v = novo[k]
        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) delete novo[k]
      }
      return novo
    })
    setPublicoSujo(true)
  }

  function alternarLista(chave: 'segmento' | 'estagio', valor: string): void {
    const atual = publico[chave] ?? []
    mudarPublico({
      [chave]: atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor],
    })
  }

  // -------------------------------------------------------------- outras ---
  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data: landings } = useQuery({
    queryKey: ['landings'],
    queryFn: () =>
      api.get<Lista<{ id: string; titulo: string; slug: string }>>('/api/v1/landing'),
  })

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () =>
      api.get<
        Lista<{
          id: string
          nome: string
          canal: string
          assunto: string | null
          corpoHtml: string | null
          corpoTexto: string
        }>
      >('/api/v1/templates'),
  })

  const [filtroEnvio, setFiltroEnvio] = useState('')
  const { data: envios } = useQuery({
    queryKey: ['campanha-envios', id, filtroEnvio],
    queryFn: () =>
      api.get<{
        dados: Envio[]
        total: number
        porStatus: Record<string, number>
      }>(`/api/v1/campanhas/${id}/envios?porPagina=20${filtroEnvio ? `&status=${filtroEnvio}` : ''}`),
  })

  const mudarStatus = useMutation({
    mutationFn: (status: string) =>
      api.put<Item<Campanha>>(`/api/v1/campanhas/${id}/status`, { status }),
    onSuccess: () => {
      r.setErro(null)
      void qc.invalidateQueries({ queryKey: ['campanha', id] })
      void qc.invalidateQueries({ queryKey: ['campanhas'] })
    },
    onError: (e: Error) => r.setErro(e.message),
  })

  const simular = useMutation({
    mutationFn: () => api.post<Item<ResultadoSimulacao>>(`/api/v1/campanhas/${id}/simular`, {}),
    onSuccess: (resp) => {
      setSimulacao(resp.dados)
      r.setErro(null)
    },
    onError: (e: Error) => r.setErro(e.message),
  })

  const disparar = useMutation({
    mutationFn: () =>
      api.post<Item<{ entrada: { candidatos: number; inscritos: number; jaEstavam: number } }>>(
        `/api/v1/campanhas/${id}/disparar`,
        { confirmacao },
      ),
    onSuccess: (resp) => {
      setConfirmando(false)
      setConfirmacao('')
      setResultadoDisparo(resp.dados.entrada)
      void qc.invalidateQueries({ queryKey: ['campanha', id] })
      void qc.invalidateQueries({ queryKey: ['campanha-envios', id] })
    },
    onError: (e: Error) => r.setErro(e.message),
  })

  function voltar(): void {
    if (
      (r.sujo || publicoSujo) &&
      !window.confirm('Há alterações não salvas nesta campanha. Sair mesmo assim?')
    ) {
      return
    }
    void navegar('/campanhas')
  }

  if (r.carregando) return <Carregando />

  // 404 de verdade — a campanha foi apagada ou o link é antigo. Ficar em
  // "Carregando…" para sempre foi exatamente o bug: quem abriu uma aba velha
  // não tinha como saber o que houve.
  if (!campanha) {
    return (
      <Vazio
        titulo="Campanha não encontrada"
        descricao={
          r.falha ??
          'Ela pode ter sido removida, ou o endereço veio de uma versão antiga da lista.'
        }
        acao={
          <Link to="/campanhas">
            <Botao>Ver as campanhas</Botao>
          </Link>
        }
      />
    )
  }

  const resumo = previaPublico?.dados.resumo
  const amostra = previaPublico?.dados.amostra ?? []
  const canalIndisponivel = campanha.canais.filter(
    (c) => !c.disponivel && r.validacao.canais.includes(c.canal as 'email' | 'sms' | 'whatsapp'),
  )
  const porStatusEnvio = envios?.porStatus ?? {}
  const totalEnvios = Object.values(porStatusEnvio).reduce((a, b) => a + b, 0)

  return (
    <div className="space-y-6">
      {/* ---------------------------------------------------------- cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={voltar}
            className="mb-1 text-sm text-slate-500 hover:text-slate-900"
          >
            ← Campanhas
          </button>
          <h2 className="text-xl font-semibold text-slate-900">{campanha.nome}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Etiqueta tom={TONS_STATUS[campanha.status] ?? 'neutro'}>{campanha.status}</Etiqueta>
            <span className="text-sm text-slate-500 capitalize">{campanha.classeComunicacao}</span>
            <span className="text-sm text-slate-500">
              {campanha.execucoes.total > 0
                ? `${n(campanha.execucoes.total)} na régua · ${n(campanha.execucoes.agendadas)} em andamento`
                : 'ninguém na régua ainda'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Botao
            variante="secundario"
            disabled={!r.sujo || r.somenteLeitura}
            carregando={r.salvar.isPending}
            onClick={() => r.salvar.mutate()}
          >
            {r.sujo ? 'Salvar régua' : 'Régua salva'}
          </Botao>

          {campanha.status !== 'ativa' && !r.somenteLeitura && (
            <Botao
              variante="secundario"
              disabled={r.sujo}
              carregando={mudarStatus.isPending}
              onClick={() => mudarStatus.mutate('ativa')}
            >
              Ativar
            </Botao>
          )}
          {campanha.status === 'ativa' && (
            <Botao
              variante="secundario"
              carregando={mudarStatus.isPending}
              onClick={() => mudarStatus.mutate('pausada')}
            >
              Pausar
            </Botao>
          )}

          <Botao
            variante="secundario"
            carregando={simular.isPending}
            onClick={() => simular.mutate()}
          >
            Simular
          </Botao>
          <Botao
            disabled={campanha.status !== 'ativa' || r.sujo || publicoSujo || !r.validacao.valida}
            title={
              campanha.status !== 'ativa'
                ? 'Ative a campanha antes de disparar'
                : r.sujo || publicoSujo
                  ? 'Salve as alterações antes de disparar'
                  : !r.validacao.valida
                    ? 'Resolva as pendências da régua'
                    : undefined
            }
            onClick={() => setConfirmando(true)}
          >
            Disparar
          </Botao>
        </div>
      </div>

      {r.erro && <Aviso tom="perigo">{r.erro}</Aviso>}

      {(r.sujo || publicoSujo) && (
        <Aviso tom="atencao">
          Há alterações não salvas
          {r.sujo && publicoSujo
            ? ' na régua e no público'
            : r.sujo
              ? ' na régua'
              : ' no público'}
          . Ativar, simular e disparar usam a versão salva no servidor.
        </Aviso>
      )}

      {resultadoDisparo && (
        <Aviso tom="sucesso" titulo="Disparo feito">
          {n(resultadoDisparo.inscritos)} entraram na régua agora
          {resultadoDisparo.jaEstavam > 0 &&
            ` · ${n(resultadoDisparo.jaEstavam)} já estavam e não entram de novo`}
          . Acompanhe nos envios abaixo.
        </Aviso>
      )}

      {r.problemasGerais.length > 0 && (
        <Aviso tom="perigo" titulo="A régua ainda não pode ser usada">
          <ul className="list-inside list-disc">
            {r.problemasGerais.map((p) => (
              <li key={p.codigo}>{p.mensagem}</li>
            ))}
          </ul>
        </Aviso>
      )}

      {canalIndisponivel.length > 0 && (
        <Aviso tom="atencao" titulo="Canal sem provedor">
          {canalIndisponivel.map((c) => (
            <p key={c.canal}>{c.motivo}</p>
          ))}
        </Aviso>
      )}

      {/* ------------------------------------------------------------ público */}
      <Cartao
        titulo="Quem recebe"
        descricao="Os filtros valem contra a base real — os números ao lado são o público de verdade, agora."
        acoes={
          publicoSujo ? (
            <Botao
              tamanho="sm"
              carregando={salvarPublico.isPending}
              onClick={() => salvarPublico.mutate()}
            >
              Salvar público
            </Botao>
          ) : undefined
        }
      >
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="space-y-4">
            <div>
              <p className="rotulo">Segmento (obrigação legal)</p>
              <div className="flex flex-wrap gap-2">
                {SEGMENTOS.map((s) => {
                  const ativo = (publico.segmento ?? []).includes(s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => alternarLista('segmento', s.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition ${
                        ativo
                          ? 'border-marca-500 bg-marca-50 font-medium text-marca-800'
                          : 'border-[color:var(--color-borda)] text-slate-600 hover:border-marca-300'
                      }`}
                    >
                      {s.nome}
                    </button>
                  )
                })}
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Nenhum marcado = todos os segmentos.
              </p>
              {/* O rótulo de segmento vem do CNAE da Receita e apanha
                  consultoria com curso livre no CNAE secundário. Para cobrar a
                  Res. 44/2020, o filtro honesto é o Censo. */}
              <label className="mt-3 flex items-start gap-2.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={publico.inep === true}
                  onChange={(e) => mudarPublico({ inep: e.target.checked ? true : undefined })}
                  className="mt-0.5 size-4"
                />
                <span>
                  Só escolas confirmadas no Censo Escolar (INEP)
                  <span className="block text-xs text-slate-500">
                    O CNPJ precisa bater com uma escola em atividade no Censo — corta quem só tem
                    CNAE de educação no papel.
                  </span>
                </span>
              </label>
            </div>

            <div>
              <p className="rotulo">Situação da empresa</p>
              <div className="flex flex-wrap gap-2">
                {ESTAGIOS.map((e) => {
                  const ativo = (publico.estagio ?? []).includes(e.valor)
                  return (
                    <button
                      key={e.valor}
                      type="button"
                      onClick={() => alternarLista('estagio', e.valor)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition ${
                        ativo
                          ? 'border-marca-500 bg-marca-50 font-medium text-marca-800'
                          : 'border-[color:var(--color-borda)] text-slate-600 hover:border-marca-300'
                      }`}
                    >
                      {e.texto}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Selecao
                rotulo="Precisa ser alcançável por"
                value={publico.canal ?? ''}
                onChange={(e) =>
                  mudarPublico({
                    canal: (e.target.value || undefined) as DefinicaoPublico['canal'],
                  })
                }
                opcoes={[
                  { valor: '', texto: 'Qualquer contato' },
                  { valor: 'email', texto: 'E-mail' },
                  { valor: 'whatsapp', texto: 'WhatsApp / telefone' },
                  { valor: 'sms', texto: 'SMS / telefone' },
                ]}
              />
              <Selecao
                rotulo="Categoria"
                value={publico.categoriaId ?? ''}
                onChange={(e) => mudarPublico({ categoriaId: e.target.value || undefined })}
                opcoes={[
                  { valor: '', texto: 'Todas' },
                  ...(categorias?.dados ?? []).map((c) => ({ valor: c.id, texto: c.nome })),
                ]}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { rotulo: 'No público', valor: resumo?.total },
                { rotulo: 'Com e-mail', valor: resumo?.comEmail },
                { rotulo: 'Opt-in WhatsApp', valor: resumo?.optinWhatsapp },
              ].map((i) => (
                <div
                  key={i.rotulo}
                  className="rounded-lg border border-[color:var(--color-borda)] p-3"
                >
                  <p className="text-xs text-slate-500">{i.rotulo}</p>
                  <p
                    className={`mt-0.5 text-xl font-semibold tabular ${contandoPublico ? 'text-slate-300' : 'text-slate-900'}`}
                  >
                    {i.valor === undefined ? '—' : n(i.valor)}
                  </p>
                </div>
              ))}
            </div>

            {amostra.length > 0 && (
              <div>
                <p className="rotulo">Amostra de quem entra</p>
                <ul className="divide-y divide-[color:var(--color-borda-suave)] rounded-lg border border-[color:var(--color-borda)]">
                  {amostra.slice(0, 6).map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 truncate text-sm text-slate-700">
                        {a.razaoSocial ?? a.nome ?? '—'}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400">
                        {[a.municipio, a.email ? '✉' : null, a.optinWhatsapp ? '💬' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {resumo && resumo.total === 0 && (
              <Aviso tom="atencao">
                Nenhuma empresa casa com estes filtros — o disparo não alcançaria ninguém.
              </Aviso>
            )}
          </div>
        </div>
      </Cartao>

      {/* ---------------------------------------------------------- simulação */}
      {simulacao && (
        <Cartao
          titulo="Simulação"
          descricao="Rodou todas as verificações contra o público real e não gravou nada."
        >
          {simulacao.bloqueioEleitoral && (
            <Aviso tom="perigo" titulo="A janela eleitoral bloqueia esta campanha">
              {simulacao.bloqueioEleitoral.fundamento}
            </Aviso>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {[
              { rotulo: 'No público', valor: simulacao.resumo.total },
              { rotulo: 'Receberiam', valor: simulacao.enviados },
              { rotulo: 'Bloqueados', valor: simulacao.bloqueados + simulacao.falhas },
            ].map((i) => (
              <div key={i.rotulo} className="rounded-lg border border-[color:var(--color-borda)] p-4">
                <p className="text-sm text-slate-500">{i.rotulo}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900 tabular">{n(i.valor)}</p>
              </div>
            ))}
          </div>
          {Object.keys(simulacao.porMotivo).length > 0 && (
            <ul className="mt-4 space-y-1 text-sm text-slate-600">
              {Object.entries(simulacao.porMotivo).map(([motivo, qtd]) => (
                <li
                  key={motivo}
                  className="flex justify-between border-b border-[color:var(--color-borda-suave)] py-1.5 last:border-0"
                >
                  <span>{NOMES_MOTIVO[motivo] ?? motivo}</span>
                  <span className="tabular text-slate-800">{n(qtd)}</span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      )}

      {/* -------------------------------------------------------------- régua */}
      {/* Canvas em largura total; o editor da etapa flutua POR CIMA, encostado
          à direita, só quando há etapa selecionada. A coluna fixa de antes
          desperdiçava a largura o tempo todo e sumia da vista ao rolar — o
          menu da caixa ficava "escondido na direita". */}
      <div className="space-y-3">
        {!r.somenteLeitura && (
          <div className="flex flex-wrap items-center gap-2">
            {PALETA.map((p) => (
              <Botao
                key={p.tipo}
                variante="secundario"
                tamanho="sm"
                onClick={() => r.adicionar(p.tipo)}
              >
                + {p.nome}
              </Botao>
            ))}
            <button
              type="button"
              title="Desfazer (Ctrl+Z)"
              disabled={!r.temHistorico}
              onClick={r.desfazer}
              className="ml-auto rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30"
            >
              ↶ Desfazer
            </button>
          </div>
        )}

        {r.etapas.length === 0 ? (
          <Vazio
            titulo="Régua vazia"
            descricao="Comece por um gatilho, ligue-o a uma ação e desenhe o resto do percurso."
          />
        ) : (
          <div className="relative">
            <Canvas
              etapas={r.etapas}
              selecionada={r.selecionada}
              problemasPorEtapa={r.problemasPorEtapa}
              somenteLeitura={r.somenteLeitura}
              onSelecionar={r.setSelecionada}
              onMover={r.mover}
              onConectar={r.conectar}
              onDesconectar={r.desconectar}
            />

            {r.selecionada && (
              <aside className="absolute top-3 right-3 z-30 flex max-h-[calc(100%-1.5rem)] w-[340px] max-w-[85%] flex-col overflow-hidden rounded-xl shadow-2xl ring-1 ring-slate-900/10">
                <div className="flex shrink-0 items-center justify-between border-b border-[color:var(--color-borda-suave)] bg-white px-4 py-2">
                  <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                    Editar etapa
                  </p>
                  <button
                    type="button"
                    title="Fechar (Esc)"
                    onClick={() => r.setSelecionada(null)}
                    className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    ✕
                  </button>
                </div>
                <div className="min-h-0 overflow-y-auto bg-white">
                  <Inspetor
                    etapa={r.etapas.find((e: Etapa) => e.id === r.selecionada) ?? null}
                    problemas={r.selecionada ? (r.problemasPorEtapa.get(r.selecionada) ?? []) : []}
                    somenteLeitura={r.somenteLeitura}
                    landings={landings?.dados ?? []}
                    templates={templates?.dados ?? []}
                    onAlterar={(config) =>
                      r.alterar(
                        r.etapas.map((e: Etapa) =>
                          e.id === r.selecionada ? { ...e, config } : e,
                        ),
                      )
                    }
                    onRemover={r.removerSelecionada}
                  />
                </div>
              </aside>
            )}
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          Clique numa etapa para editá-la no painel flutuante. <kbd>Ctrl</kbd>+<kbd>S</kbd> salva ·{' '}
          <kbd>Ctrl</kbd>+<kbd>Z</kbd> desfaz · <kbd>Delete</kbd> remove a etapa selecionada ·{' '}
          <kbd>Esc</kbd> fecha o painel.
        </p>
      </div>

      {/* ------------------------------------------------- funil do rastreio */}
      {campanha.metricas.enviados > 0 && (
        <Cartao
          titulo="Do envio ao formulário"
          descricao="Cada link sai carimbado com um id próprio; a landing marca o clique ao abrir e devolve o id quando o formulário é enviado. Medição nossa, de primeira parte — o pixel é outra camada e continua atrás do consentimento."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            {(
              [
                ['Enviados', campanha.metricas.enviados, null],
                [
                  'Clicaram no link',
                  campanha.metricas.cliques,
                  campanha.metricas.enviados > 0
                    ? Math.round((campanha.metricas.cliques / campanha.metricas.enviados) * 100)
                    : null,
                ],
                [
                  'Preencheram o formulário',
                  campanha.metricas.conversoes,
                  campanha.metricas.enviados > 0
                    ? Math.round((campanha.metricas.conversoes / campanha.metricas.enviados) * 100)
                    : null,
                ],
              ] as Array<[string, number, number | null]>
            ).map(([rotulo, valor, pct]) => (
              <div key={rotulo} className="rounded-lg border border-[color:var(--color-borda)] p-4">
                <p className="text-sm text-slate-500">{rotulo}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900 tabular">
                  {n(valor)}
                  {pct !== null && (
                    <span className="ml-2 text-sm font-normal text-slate-400">{pct}%</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </Cartao>
      )}

      {/* ------------------------------------------------- pixel e retarget */}
      <Cartao
        titulo="Pixel e retarget"
        descricao="Os links desta campanha saem carimbados com os valores abaixo. É com eles que se monta o público de retarget nas plataformas."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-2 text-sm">
            <p className="rotulo">Carimbo nos links (UTM)</p>
            {(
              [
                ['utm_source', 'egba-diario-oficial'],
                ['utm_medium', 'email · whatsapp · sms (o canal do envio)'],
                ['utm_campaign', campanha.id],
                ['utm_content', 'o id da etapa da régua'],
              ] as Array<[string, string]>
            ).map(([chave, valor]) => (
              <div
                key={chave}
                className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-borda)] px-3 py-2"
              >
                <code className="text-xs text-slate-500">{chave}</code>
                <code className="min-w-0 truncate text-xs text-slate-800">{valor}</code>
              </div>
            ))}
            <p className="text-xs leading-relaxed text-slate-400">
              Público de retarget: no Meta, audiência por tráfego do site com “URL contém
              utm_campaign={campanha.id.slice(0, 8)}…”; no GA4/Google Ads, segmento por campanha.
              O id não muda quando o nome muda — audiência montada sobre nome quebra no primeiro
              rename.
            </p>
          </div>
          <div className="space-y-2 text-sm">
            <p className="rotulo">O que o pixel da landing dispara</p>
            <ul className="space-y-1.5 text-xs leading-relaxed text-slate-600">
              <li>
                <code className="text-slate-800">view_form</code> — viu o formulário (40% visível)
              </li>
              <li>
                <code className="text-slate-800">form_start</code> — começou a preencher
              </li>
              <li>
                <code className="text-slate-800">generate_lead</code> — enviou (conversão nos Ads)
              </li>
            </ul>
            <p className="text-xs leading-relaxed text-slate-400">
              Disparam nos provedores configurados em{' '}
              <Link to="/configuracoes" className="font-medium text-marca-700 underline">
                Configurações → Medição
              </Link>{' '}
              e nos pixels da própria página — sempre depois do consentimento na barra de cookies.
              Quem recusa não entra em público nenhum; o funil acima não depende disso.
            </p>
          </div>
        </div>
      </Cartao>

      {/* -------------------------------------------------------------- envios */}
      <Cartao
        titulo="Envios"
        descricao="O que de fato saiu — ou foi segurado — por esta campanha, mensagem a mensagem."
      >
        {totalEnvios === 0 ? (
          <Vazio
            titulo="Nenhum envio ainda"
            descricao="Quando a campanha disparar, cada mensagem aparece aqui com o que aconteceu com ela — enviada, bloqueada e por quê."
          />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFiltroEnvio('')}
                className={`rounded-full border px-3 py-1 text-sm transition ${
                  filtroEnvio === ''
                    ? 'border-marca-500 bg-marca-50 font-medium text-marca-800'
                    : 'border-[color:var(--color-borda)] text-slate-600'
                }`}
              >
                Todos · {n(totalEnvios)}
              </button>
              {Object.entries(porStatusEnvio).map(([s, qtd]) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFiltroEnvio(s)}
                  className={`rounded-full border px-3 py-1 text-sm capitalize transition ${
                    filtroEnvio === s
                      ? 'border-marca-500 bg-marca-50 font-medium text-marca-800'
                      : 'border-[color:var(--color-borda)] text-slate-600'
                  }`}
                >
                  {s} · {n(qtd)}
                </button>
              ))}
            </div>

            <Tabela>
              <Cabecalho colunas={['Destinatário', 'Canal', 'Assunto', 'Situação', 'Quando']} />
              <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
                {(envios?.dados ?? []).map((e) => (
                  <tr key={e.id}>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-800">
                        {e.lead?.razaoSocial ?? e.lead?.nome ?? '—'}
                      </p>
                      {e.lead?.email && <p className="text-xs text-slate-400">{e.lead.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 capitalize">{e.canal}</td>
                    <td className="max-w-[260px] truncate px-4 py-3 text-sm text-slate-600">
                      {e.assunto ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Etiqueta tom={TONS_ENVIO[e.status] ?? 'neutro'}>{e.status}</Etiqueta>
                        {e.clicadoEm && (
                          <span
                            title={`Abriu a landing em ${formatarData(e.clicadoEm)}`}
                            className="rounded-full bg-marca-50 px-2 py-0.5 text-[11px] font-medium text-marca-700"
                          >
                            clicou
                          </span>
                        )}
                        {e.convertidoEm && (
                          <span
                            title={`Preencheu o formulário em ${formatarData(e.convertidoEm)}`}
                            className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                          >
                            preencheu
                          </span>
                        )}
                      </div>
                      {e.motivoBloqueio && (
                        <p className="mt-1 text-xs text-slate-400">
                          {NOMES_MOTIVO[e.motivoBloqueio] ?? e.motivoBloqueio}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap text-slate-500">
                      {formatarData(e.enviadoEm ?? e.criadoEm)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            {(envios?.total ?? 0) > 20 && (
              <p className="mt-3 text-xs text-slate-400">
                Mostrando os 20 mais recentes de {n(envios!.total)}.
              </p>
            )}
          </>
        )}
      </Cartao>

      {/* ----------------------------------------------------------- confirmar */}
      <Modal aberto={confirmando} aoFechar={() => setConfirmando(false)} titulo="Disparar campanha">
        <p className="text-sm text-slate-600">
          Os leads do público entram na régua e passam a receber os envios desenhados. Quem já está
          na régua não entra de novo.
        </p>
        {resumo && (
          <Aviso tom="info">
            O público salvo alcança <strong>{n(resumo.total)}</strong> empresas —{' '}
            {n(resumo.comEmail)} com e-mail.
            {simulacao &&
              ` A última simulação indicou ${n(simulacao.enviados)} envios e ${n(
                simulacao.bloqueados + simulacao.falhas,
              )} bloqueios no primeiro passo.`}
          </Aviso>
        )}
        {!simulacao && (
          <Aviso tom="atencao">
            Você ainda não simulou. A simulação mostra exatamente quem receberia e quem seria
            bloqueado, sem enviar nada.
          </Aviso>
        )}
        <div className="mt-4">
          <Campo
            rotulo={`Digite “${campanha.nome}” para confirmar`}
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            ajuda="Confirmação por digitação — “tem certeza?” deixou de proteger alguém há muito tempo."
          />
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setConfirmando(false)}>
            Cancelar
          </Botao>
          <Botao
            carregando={disparar.isPending}
            disabled={confirmacao.trim() !== campanha.nome.trim()}
            onClick={() => disparar.mutate()}
          >
            Disparar
          </Botao>
        </div>
      </Modal>
    </div>
  )
}
